// src/data/regions/fishIQUnitedStatesSpeciesBuffV2.ts
// Roddo FishIQ U.S. catalog buff v2.
// Static/editorial guidance only: likely patterns, habitat associations, and education layer.
// Not a regulation source, not official biology, and not exact-location guidance.

import type { FishIQSpeciesSeed } from '../../types';

type FishIQTier = 'common_target' | 'local_common' | 'trophy' | 'education_sensitive';
type RoddoFishIQSeed = FishIQSpeciesSeed & { fishIQTier?: FishIQTier };

const source = { title: 'Roddo U.S. species expansion v2', sourceType: 'editorial' as const };

const species = (input: RoddoFishIQSeed): FishIQSpeciesSeed => ({
  confidence: 'editorial',
  editorialSources: [source],
  ...input,
});

export const US_FISHIQ_SPECIES_BUFF_V2_SEED: FishIQSpeciesSeed[] = [
  species({
    id: "chain_pickerel",
    slug: "chain-pickerel",
    commonName: "Chain Pickerel",
    scientificName: "Esox niger",
    aliases: [],
    family: "pike",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["pond", "lake", "river", "swamp"],
    regions: ["Eastern United States", "Southeast", "Northeast"],
    summary: "A sharp-toothed ambush predator often tied to weeds, pads, tannic water, and shallow cover.",
    feedingProfile: { primaryPrey: ["minnows", "sunfish", "frogs"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Spinnerbait", "Spoon", "Jerkbait", "Inline Spinner"],
    bestTechniques: ["Work weed edges", "Pause near ambush holes", "Use a leader around toothy fish"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["ambush predator", "cover-oriented"],
    structureTags: ["weeds", "pads", "wood", "shallow edges"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["ambush predator", "cover-oriented"],
  }),
  species({
    id: "grass_pickerel",
    slug: "grass-pickerel",
    commonName: "Grass Pickerel",
    scientificName: "Esox americanus vermiculatus",
    aliases: [],
    family: "pike",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["creek", "pond", "marsh", "backwater"],
    regions: ["Midwest", "Great Lakes", "Eastern United States"],
    summary: "A smaller pickerel of weedy creeks, marshes, and quiet backwaters; useful as a local discovery species.",
    feedingProfile: { primaryPrey: ["minnows", "insects", "small fish"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Inline Spinner", "Small Spoon", "Tiny Swimbait", "Minnow"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["ambush predator", "small-water cover"],
    structureTags: ["weeds", "undercut banks", "backwater pockets"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["ambush predator", "small-water cover"],
  }),
  species({
    id: "freshwater_drum",
    slug: "freshwater-drum",
    commonName: "Freshwater Drum",
    scientificName: "Aplodinotus grunniens",
    aliases: ["Gaspergou"],
    family: "drum",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["river", "lake", "reservoir"],
    regions: ["Great Lakes", "Mississippi River Basin", "Midwest", "South"],
    summary: "A widespread native drum that often feeds along current, rock, mussel beds, and bottom transitions.",
    feedingProfile: { primaryPrey: ["mussels", "crayfish", "invertebrates", "small fish"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Worm", "Minnow", "Crayfish", "Jig"],
    bestTechniques: ["Fish bottom transitions", "Work current breaks", "Use natural bait around rock"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["bottom feeder", "current-aware feeder"],
    structureTags: ["rock", "mussel beds", "channels", "current seams"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["bottom feeder", "current-aware feeder"],
  }),
  species({
    id: "shorthead_redhorse",
    slug: "shorthead-redhorse",
    commonName: "Shorthead Redhorse",
    scientificName: "Moxostoma macrolepidotum",
    aliases: [],
    family: "sucker",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["river", "creek", "lake"],
    regions: ["Midwest", "Great Lakes", "Mississippi River Basin"],
    summary: "A native sucker often found in clean rivers and lake margins; good education layer for bottom-oriented local fish.",
    feedingProfile: { primaryPrey: ["invertebrates", "small mollusks", "larvae"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Worm", "Small Nymph", "Dough Bait"],
    bestTechniques: ["Fish clean gravel runs", "Use light bottom rigs", "Avoid overplaying fish"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["bottom feeder", "native rough fish"],
    structureTags: ["gravel runs", "current seams", "soft edges"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["bottom feeder", "native rough fish"],
  }),
  species({
    id: "golden_redhorse",
    slug: "golden-redhorse",
    commonName: "Golden Redhorse",
    scientificName: "Moxostoma erythrurum",
    aliases: [],
    family: "sucker",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["river", "creek"],
    regions: ["Midwest", "Great Lakes", "Eastern United States"],
    summary: "A river-oriented sucker that adds realistic native diversity to FishIQ reads around clean current and gravel.",
    feedingProfile: { primaryPrey: ["aquatic insects", "mollusks", "detritus"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Worm", "Small Nymph", "Natural Bait"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["bottom feeder", "native rough fish"],
    structureTags: ["gravel", "riffles", "current seams"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["bottom feeder", "native rough fish"],
  }),
  species({
    id: "quillback",
    slug: "quillback",
    commonName: "Quillback",
    scientificName: "Carpiodes cyprinus",
    aliases: [],
    family: "sucker",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["river", "lake", "reservoir"],
    regions: ["Central United States", "Great Lakes", "Mississippi River Basin"],
    summary: "A carpsucker often associated with slow rivers, reservoirs, and soft-bottom feeding zones.",
    feedingProfile: { primaryPrey: ["invertebrates", "detritus", "small mollusks"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Worm", "Dough Bait", "Small Natural Bait"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["bottom feeder", "schooling"],
    structureTags: ["soft bottom", "channels", "flats"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["bottom feeder", "schooling"],
  }),
  species({
    id: "bigmouth_buffalo",
    slug: "bigmouth-buffalo",
    commonName: "Bigmouth Buffalo",
    scientificName: "Ictiobus cyprinellus",
    aliases: [],
    family: "sucker",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["river", "lake", "reservoir", "backwater"],
    regions: ["Mississippi River Basin", "Midwest", "South"],
    summary: "A large native filter-feeding sucker often confused with carp; useful for education and careful ID.",
    feedingProfile: { primaryPrey: ["plankton", "detritus", "small invertebrates"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Dough Bait", "Corn", "Small Natural Bait"],
    bestTechniques: ["Fish slow backwaters", "Use careful species ID", "Avoid assuming every large rough fish is carp"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["filter feeder", "education"],
    structureTags: ["backwaters", "flats", "channels"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["filter feeder", "education"],
  }),
  species({
    id: "smallmouth_buffalo",
    slug: "smallmouth-buffalo",
    commonName: "Smallmouth Buffalo",
    scientificName: "Ictiobus bubalus",
    aliases: [],
    family: "sucker",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["river", "reservoir", "lake"],
    regions: ["Central United States", "South", "Mississippi River Basin"],
    summary: "A bottom-feeding native buffalo species that helps Roddo separate local rough fish from generic carp reads.",
    feedingProfile: { primaryPrey: ["invertebrates", "mollusks", "detritus"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Worm", "Dough Bait", "Natural Bait"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["bottom feeder", "education"],
    structureTags: ["soft bottom", "channels", "flats"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["bottom feeder", "education"],
  }),
  species({
    id: "river_carpsucker",
    slug: "river-carpsucker",
    commonName: "River Carpsucker",
    scientificName: "Carpiodes carpio",
    aliases: [],
    family: "sucker",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["river", "reservoir"],
    regions: ["Great Plains", "Mississippi River Basin", "Southwest"],
    summary: "A current-tolerant native carpsucker often found in turbid rivers and reservoir arms.",
    feedingProfile: { primaryPrey: ["detritus", "invertebrates", "mollusks"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Worm", "Dough Bait", "Small Natural Bait"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["bottom feeder", "river native"],
    structureTags: ["current seams", "soft bottom", "channel edges"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["bottom feeder", "river native"],
  }),
  species({
    id: "creek_chub",
    slug: "creek-chub",
    commonName: "Creek Chub",
    scientificName: "Semotilus atromaculatus",
    aliases: [],
    family: "minnow",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["creek", "river"],
    regions: ["Eastern United States", "Midwest", "Great Lakes"],
    summary: "A common creek fish and forage species; useful for small-water education and baitfish context.",
    feedingProfile: { primaryPrey: ["insects", "small invertebrates", "tiny fish"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Tiny Jig", "Worm", "Micro Spinner"],
    bestTechniques: ["Fish pools and undercut banks", "Use ultralight tackle", "Observe forage before predator fishing"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["forage fish", "small-water"],
    structureTags: ["pools", "undercut banks", "riffle edges"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["forage fish", "small-water"],
  }),
  species({
    id: "fallfish",
    slug: "fallfish",
    commonName: "Fallfish",
    scientificName: "Semotilus corporalis",
    aliases: [],
    family: "minnow",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["river", "creek"],
    regions: ["Northeast", "Appalachia", "Eastern United States"],
    summary: "A large native minnow of clean rivers and streams; a realistic local species for Northeast small-water logs.",
    feedingProfile: { primaryPrey: ["insects", "small fish", "invertebrates"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Small Spinner", "Worm", "Dry Fly", "Nymph"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["opportunistic feeder", "current-aware feeder"],
    structureTags: ["riffles", "pools", "current seams"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["opportunistic feeder", "current-aware feeder"],
  }),
  species({
    id: "round_goby",
    slug: "round-goby",
    commonName: "Round Goby",
    scientificName: "Neogobius melanostomus",
    aliases: [],
    family: "goby",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "education_sensitive",
    habitats: ["lake", "river", "rock"],
    regions: ["Great Lakes", "Introduced US waters"],
    summary: "An introduced bottom fish common in parts of the Great Lakes, important as forage and ID context.",
    feedingProfile: { primaryPrey: ["mussels", "invertebrates", "fish eggs"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Tiny Jig", "Worm", "Micro Plastic"],
    bestTechniques: ["Use for ID/forage context", "Look around rock and harbor edges", "Avoid moving live fish between waters"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["bottom feeder", "introduced species"],
    structureTags: ["rock", "harbors", "breakwalls"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["bottom feeder", "introduced species"],
  }),
  species({
    id: "rudd",
    slug: "rudd",
    commonName: "Rudd",
    scientificName: "Scardinius erythrophthalmus",
    aliases: [],
    family: "cyprinid",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "education_sensitive",
    habitats: ["pond", "lake", "canal"],
    regions: ["Introduced US waters", "Northeast", "Midwest"],
    summary: "An introduced cyprinid that can appear in ponds/canals; helpful for ID, not a primary target recommendation.",
    feedingProfile: { primaryPrey: ["plants", "insects", "invertebrates"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Bread", "Corn", "Tiny Fly", "Worm"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["schooling", "introduced species"],
    structureTags: ["weeds", "canal edges", "pond margins"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["schooling", "introduced species"],
  }),
  species({
    id: "peacock_bass_us",
    slug: "peacock-bass-us",
    commonName: "Peacock Bass",
    scientificName: "Cichla ocellaris / Cichla spp.",
    aliases: ["Butterfly Peacock Bass"],
    family: "cichlid",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["canal", "lake", "pond"],
    regions: ["South Florida", "Introduced warmwater fisheries"],
    summary: "A tropical sight-feeding predator established in limited warmwater U.S. canals and lakes, especially South Florida.",
    feedingProfile: { primaryPrey: ["small fish", "shiners", "minnows"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Jerkbait", "Topwater", "Live Shiner", "Small Swimbait"],
    bestTechniques: ["Fish clear canals", "Use reaction baits", "Look for bait schools and ambush shade"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["visual predator", "ambush predator"],
    structureTags: ["canals", "bridges", "shade lines", "weeds"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["visual predator", "ambush predator"],
  }),
  species({
    id: "oscar",
    slug: "oscar",
    commonName: "Oscar",
    scientificName: "Astronotus ocellatus",
    aliases: [],
    family: "cichlid",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["canal", "pond", "marsh"],
    regions: ["South Florida", "Introduced warmwater fisheries"],
    summary: "A colorful introduced cichlid commonly caught in some South Florida canals and marsh edges.",
    feedingProfile: { primaryPrey: ["insects", "small fish", "crustaceans"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Worm", "Small Jig", "Cricket", "Bread"],
    bestTechniques: ["Fish canal edges", "Use small natural baits", "Watch for aggressive visual eats"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["opportunistic feeder", "warmwater cichlid"],
    structureTags: ["canals", "weeds", "edges"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["opportunistic feeder", "warmwater cichlid"],
  }),
  species({
    id: "maya_cichlid",
    slug: "maya-cichlid",
    commonName: "Mayan Cichlid",
    scientificName: "Mayaheros urophthalmus",
    aliases: ["Mayan Cichlid"],
    family: "cichlid",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["canal", "pond", "brackish", "marsh"],
    regions: ["South Florida", "Introduced warmwater fisheries"],
    summary: "A hardy cichlid often found in warm canals, ponds, and brackish edges; good local FishIQ diversity for Florida.",
    feedingProfile: { primaryPrey: ["insects", "small fish", "crustaceans"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Worm", "Small Jig", "Cricket", "Soft Plastic"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["opportunistic feeder", "warmwater cichlid"],
    structureTags: ["canals", "mangrove edges", "weeds"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["opportunistic feeder", "warmwater cichlid"],
  }),
  species({
    id: "redbreast_tilapia",
    slug: "redbreast-tilapia",
    commonName: "Redbreast Tilapia",
    scientificName: "Coptodon rendalli",
    aliases: [],
    family: "cichlid",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["canal", "pond", "lake"],
    regions: ["Introduced US waters", "South Florida", "Warmwater ponds"],
    summary: "An introduced tilapia species that may show around warm ponds and canals; useful for local ID context.",
    feedingProfile: { primaryPrey: ["plants", "algae", "invertebrates"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Bread", "Corn", "Tiny Jig", "Worm"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["warmwater cichlid", "vegetation-oriented"],
    structureTags: ["weeds", "pond margins", "canal edges"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["warmwater cichlid", "vegetation-oriented"],
  }),
  species({
    id: "redeye_bass",
    slug: "redeye-bass",
    commonName: "Redeye Bass",
    scientificName: "Micropterus coosae",
    aliases: [],
    family: "black bass",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["river", "creek", "reservoir"],
    regions: ["Southeast", "Appalachia"],
    summary: "A smaller black bass tied to clear rocky rivers and upland streams in the Southeast.",
    feedingProfile: { primaryPrey: ["crayfish", "minnows", "insects"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Small Crankbait", "Ned Rig", "Tube Jig", "Topwater"],
    bestTechniques: ["Fish rocky shoals", "Use smaller natural profiles", "Work current breaks"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["current-aware feeder", "ambush predator"],
    structureTags: ["rock", "shoals", "current seams"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["current-aware feeder", "ambush predator"],
  }),
  species({
    id: "shoal_bass",
    slug: "shoal-bass",
    commonName: "Shoal Bass",
    scientificName: "Micropterus cataractae",
    aliases: [],
    family: "black bass",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["river", "shoal", "creek"],
    regions: ["Southeast", "Georgia", "Florida Panhandle"],
    summary: "A river black bass associated with shoals, current, and rocky habitat; strong regional identity species.",
    feedingProfile: { primaryPrey: ["crayfish", "minnows", "insects"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Tube Jig", "Crankbait", "Topwater", "Ned Rig"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["current-aware feeder", "river bass"],
    structureTags: ["shoals", "rock", "current seams"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["current-aware feeder", "river bass"],
  }),
  species({
    id: "guadalupe_bass",
    slug: "guadalupe-bass",
    commonName: "Guadalupe Bass",
    scientificName: "Micropterus treculii",
    aliases: [],
    family: "black bass",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["river", "creek"],
    regions: ["Texas Hill Country"],
    summary: "A Texas native river bass tied to clear current, rock, and small-stream structure.",
    feedingProfile: { primaryPrey: ["crayfish", "minnows", "insects"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Small Crankbait", "Ned Rig", "Topwater", "Spinner"],
    bestTechniques: ["Fish current seams", "Use smaller river-bass presentations", "Respect native bass ID complexity"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["current-aware feeder", "native bass"],
    structureTags: ["rock", "riffles", "current seams"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["current-aware feeder", "native bass"],
  }),
  species({
    id: "alabama_bass",
    slug: "alabama-bass",
    commonName: "Alabama Bass",
    scientificName: "Micropterus henshalli",
    aliases: [],
    family: "black bass",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "education_sensitive",
    habitats: ["reservoir", "river", "lake"],
    regions: ["Southeast", "Introduced US waters"],
    summary: "A spotted-bass relative that can be a strong reservoir predator, with important ID and native-bass context in some regions.",
    feedingProfile: { primaryPrey: ["shad", "crayfish", "minnows"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Swimbait", "Drop Shot", "Jerkbait", "Underspin"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["bait-tracker", "reservoir bass"],
    structureTags: ["points", "brush", "rock", "bait schools"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["bait-tracker", "reservoir bass"],
  }),
  species({
    id: "northern_strain_largemouth",
    slug: "northern-strain-largemouth",
    commonName: "Northern-strain Largemouth Bass",
    scientificName: "Micropterus salmoides salmoides",
    aliases: [],
    family: "black bass",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["lake", "pond", "river", "reservoir"],
    regions: ["United States", "Canada border states"],
    summary: "A useful regional variant profile for cooler-water largemouth patterns in northern lakes, ponds, and rivers.",
    feedingProfile: { primaryPrey: ["bluegill", "shad", "crayfish"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Plastic Worm", "Jig", "Spinnerbait", "Frog"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["ambush predator", "cover-oriented"],
    structureTags: ["weeds", "wood", "docks", "points"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["ambush predator", "cover-oriented"],
  }),
  species({
    id: "florida_strain_largemouth",
    slug: "florida-strain-largemouth",
    commonName: "Florida-strain Largemouth Bass",
    scientificName: "Micropterus salmoides floridanus",
    aliases: [],
    family: "black bass",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["lake", "pond", "canal", "reservoir"],
    regions: ["Florida", "Southeast", "Stocked trophy waters"],
    summary: "A warmwater largemouth variant important for Florida and trophy-stocked fisheries, without replacing the generic largemouth profile.",
    feedingProfile: { primaryPrey: ["shiners", "bluegill", "shad", "crayfish"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Live Shiner", "Big Worm", "Swimbait", "Topwater Frog"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["ambush predator", "trophy potential"],
    structureTags: ["vegetation", "pads", "hydrilla", "edges"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["ambush predator", "trophy potential"],
  }),
  species({
    id: "tautog",
    slug: "tautog",
    commonName: "Tautog",
    scientificName: "Tautoga onitis",
    aliases: ["Blackfish"],
    family: "wrasse",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["reef", "inshore", "rock", "wreck"],
    regions: ["Northeast", "Mid Atlantic"],
    summary: "A structure-loving Northeast/Mid-Atlantic fish famous for crab baits, rocks, wrecks, and subtle bites.",
    feedingProfile: { primaryPrey: ["crabs", "mussels", "clams"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Green Crab", "Asian Crab", "Clam", "Shrimp"],
    bestTechniques: ["Fish tight to structure", "Use strong leaders", "Detect subtle bites"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["structure-bound", "crustacean feeder"],
    structureTags: ["rocks", "wrecks", "jetties", "pilings"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["structure-bound", "crustacean feeder"],
  }),
  species({
    id: "black_sea_bass",
    slug: "black-sea-bass",
    commonName: "Black Sea Bass",
    scientificName: "Centropristis striata",
    aliases: [],
    family: "sea bass",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["reef", "wreck", "inshore", "ocean"],
    regions: ["Atlantic Coast", "Northeast", "Mid Atlantic", "Southeast"],
    summary: "A popular structure fish found around reefs, wrecks, ledges, and bait-rich bottom.",
    feedingProfile: { primaryPrey: ["squid", "crabs", "small fish"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Squid", "Jig", "Clam", "Gulp"],
    bestTechniques: ["Drop to structure", "Use jigs or bait rigs", "Adjust for current"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["structure-oriented", "bottom feeder"],
    structureTags: ["reefs", "wrecks", "ledges"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["structure-oriented", "bottom feeder"],
  }),
  species({
    id: "summer_flounder",
    slug: "summer-flounder",
    commonName: "Summer Flounder",
    scientificName: "Paralichthys dentatus",
    aliases: ["Fluke"],
    family: "flatfish",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["bay", "inshore", "ocean", "estuary"],
    regions: ["Atlantic Coast", "Northeast", "Mid Atlantic"],
    summary: "A sand/edge ambush predator often caught by drifting baits or jigs over channels, flats, and inlets.",
    feedingProfile: { primaryPrey: ["spearing", "squid", "killifish", "baitfish"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Bucktail", "Squid Strip", "Gulp", "Live Minnow"],
    bestTechniques: ["Drift channel edges", "Keep contact near bottom", "Target current-fed sand transitions"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["ambush predator", "bottom-oriented"],
    structureTags: ["sand edges", "channels", "inlets", "drop-offs"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["ambush predator", "bottom-oriented"],
  }),
  species({
    id: "winter_flounder",
    slug: "winter-flounder",
    commonName: "Winter Flounder",
    scientificName: "Pseudopleuronectes americanus",
    aliases: [],
    family: "flatfish",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["bay", "estuary", "inshore"],
    regions: ["Northeast", "New England"],
    summary: "A cold-water flatfish associated with bays, mud/sand flats, and bottom forage.",
    feedingProfile: { primaryPrey: ["worms", "small clams", "invertebrates"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Worm", "Clam", "Small Bait Rig"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["bottom feeder", "coldwater flatfish"],
    structureTags: ["mud flats", "sand flats", "channels"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["bottom feeder", "coldwater flatfish"],
  }),
  species({
    id: "scup",
    slug: "scup",
    commonName: "Scup",
    scientificName: "Stenotomus chrysops",
    aliases: ["Porgy"],
    family: "porgy",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["reef", "bay", "inshore"],
    regions: ["Northeast", "Mid Atlantic"],
    summary: "A schooling porgy common around reefs, rocks, piers, and bait-rich inshore structure.",
    feedingProfile: { primaryPrey: ["worms", "clams", "small crustaceans"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Squid", "Clam", "Worm", "Small Jig"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["schooling", "structure-oriented"],
    structureTags: ["reefs", "rocks", "piers"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["schooling", "structure-oriented"],
  }),
  species({
    id: "northern_puffer",
    slug: "northern-puffer",
    commonName: "Northern Puffer",
    scientificName: "Sphoeroides maculatus",
    aliases: ["Blowfish"],
    family: "puffer",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["bay", "estuary", "inshore"],
    regions: ["Atlantic Coast", "Northeast", "Mid Atlantic"],
    summary: "A small inshore/bay species that adds realistic pier and bay diversity for Atlantic anglers.",
    feedingProfile: { primaryPrey: ["clams", "worms", "crustaceans"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Clam", "Squid", "Worm"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["bottom feeder", "small inshore fish"],
    structureTags: ["sand flats", "bay edges", "piers"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["bottom feeder", "small inshore fish"],
  }),
  species({
    id: "oyster_toadfish",
    slug: "oyster-toadfish",
    commonName: "Oyster Toadfish",
    scientificName: "Opsanus tau",
    aliases: [],
    family: "toadfish",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["bay", "estuary", "inshore", "reef"],
    regions: ["Atlantic Coast", "Chesapeake Bay", "Mid Atlantic"],
    summary: "A bottom-dwelling ambush fish often encountered around oyster, rock, and dock structure; useful for ID context.",
    feedingProfile: { primaryPrey: ["crabs", "shrimp", "small fish"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Cut Bait", "Shrimp", "Clam"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["ambush predator", "bottom-oriented"],
    structureTags: ["oyster beds", "rocks", "docks"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["ambush predator", "bottom-oriented"],
  }),
  species({
    id: "pigfish",
    slug: "pigfish",
    commonName: "Pigfish",
    scientificName: "Orthopristis chrysoptera",
    aliases: [],
    family: "grunt",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["bay", "inshore", "grass", "reef"],
    regions: ["Southeast Atlantic", "Gulf Coast"],
    summary: "A common grunt around grass, docks, and reefs; often part of mixed inshore catches and bait ecology.",
    feedingProfile: { primaryPrey: ["shrimp", "worms", "small crustaceans"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Shrimp", "Squid", "Small Jig"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["schooling", "bottom feeder"],
    structureTags: ["grass", "docks", "reefs"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["schooling", "bottom feeder"],
  }),
  species({
    id: "pinfish",
    slug: "pinfish",
    commonName: "Pinfish",
    scientificName: "Lagodon rhomboides",
    aliases: [],
    family: "porgy",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["grass", "bay", "estuary", "inshore"],
    regions: ["Southeast Atlantic", "Gulf Coast"],
    summary: "A very common grassflat species and forage fish; helpful for bait, ID, and local ecosystem reads.",
    feedingProfile: { primaryPrey: ["seagrass", "shrimp", "small invertebrates"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Tiny Jig", "Shrimp", "Sabiki", "Bread"],
    bestTechniques: ["Fish grass edges", "Use as forage context", "Watch baitfish behavior"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["forage fish", "schooling"],
    structureTags: ["grass flats", "docks", "bay edges"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["forage fish", "schooling"],
  }),
  species({
    id: "hardhead_catfish",
    slug: "hardhead-catfish",
    commonName: "Hardhead Catfish",
    scientificName: "Ariopsis felis",
    aliases: [],
    family: "sea catfish",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["bay", "estuary", "inshore", "surf"],
    regions: ["Gulf Coast", "Southeast Atlantic"],
    summary: "A common saltwater catfish often encountered from piers, surf, and bays; useful for realistic catch logs and ID.",
    feedingProfile: { primaryPrey: ["shrimp", "small fish", "detritus"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Shrimp", "Cut Bait", "Squid"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["bottom feeder", "scent/taste oriented"],
    structureTags: ["channels", "surf troughs", "bay bottom"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["bottom feeder", "scent/taste oriented"],
  }),
  species({
    id: "gafftopsail_catfish",
    slug: "gafftopsail-catfish",
    commonName: "Gafftopsail Catfish",
    scientificName: "Bagre marinus",
    aliases: [],
    family: "sea catfish",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["bay", "estuary", "inshore", "surf"],
    regions: ["Gulf Coast", "Southeast Atlantic"],
    summary: "A sail-finned saltwater catfish often found in warm bays and surf zones, usually around scent trails and moving water.",
    feedingProfile: { primaryPrey: ["shrimp", "small fish", "crabs"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Shrimp", "Cut Bait", "Squid"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["bottom feeder", "scent/taste oriented"],
    structureTags: ["channels", "surf troughs", "bay edges"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["bottom feeder", "scent/taste oriented"],
  }),
  species({
    id: "spanish_mackerel_us",
    slug: "spanish-mackerel-us",
    commonName: "Spanish Mackerel",
    scientificName: "Scomberomorus maculatus",
    aliases: [],
    family: "mackerel",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["inshore", "ocean", "bay", "nearshore"],
    regions: ["Atlantic Coast", "Gulf Coast"],
    summary: "A fast schooling predator that tracks small bait along beaches, piers, passes, and nearshore current lines.",
    feedingProfile: { primaryPrey: ["glass minnows", "anchovies", "small baitfish"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Spoon", "Gotcha Plug", "Small Jig", "Trolling Spoon"],
    bestTechniques: ["Cast into bait schools", "Retrieve fast", "Use light wire or heavier leader when needed"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["schooling", "bait-tracker"],
    structureTags: ["bait schools", "passes", "beach troughs", "piers"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["schooling", "bait-tracker"],
  }),
  species({
    id: "jack_crevalle",
    slug: "jack-crevalle",
    commonName: "Jack Crevalle",
    scientificName: "Caranx hippos",
    aliases: [],
    family: "jack",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["inshore", "bay", "surf", "ocean"],
    regions: ["Gulf Coast", "Southeast Atlantic", "Tropics"],
    summary: "A powerful schooling jack that often erupts on bait around beaches, passes, seawalls, and inshore channels.",
    feedingProfile: { primaryPrey: ["baitfish", "shrimp", "crabs"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Topwater Plug", "Spoon", "Swimbait", "Live Bait"],
    bestTechniques: ["Chase surface feeds", "Fish passes and seawalls", "Use durable tackle"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["schooling", "roaming predator"],
    structureTags: ["bait schools", "passes", "seawalls", "current lines"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["schooling", "roaming predator"],
  }),
  species({
    id: "blue_runner",
    slug: "blue-runner",
    commonName: "Blue Runner",
    scientificName: "Caranx crysos",
    aliases: [],
    family: "jack",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["reef", "inshore", "ocean", "pier"],
    regions: ["Gulf Coast", "Southeast Atlantic", "Tropics"],
    summary: "A smaller schooling jack common around reefs, piers, and bait schools; good local catch and forage context.",
    feedingProfile: { primaryPrey: ["small baitfish", "shrimp", "squid"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Sabiki", "Small Jig", "Spoon", "Shrimp"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["schooling", "bait-tracker"],
    structureTags: ["piers", "reefs", "bait schools"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["schooling", "bait-tracker"],
  }),
  species({
    id: "lookdown",
    slug: "lookdown",
    commonName: "Lookdown",
    scientificName: "Selene vomer",
    aliases: [],
    family: "jack",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["pier", "inshore", "bay", "surf"],
    regions: ["Atlantic Coast", "Gulf Coast"],
    summary: "A thin, flashy jack often seen around piers, lights, and inshore bait concentrations.",
    feedingProfile: { primaryPrey: ["small baitfish", "shrimp", "plankton"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Tiny Jig", "Sabiki", "Small Shrimp"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["schooling", "light-line fish"],
    structureTags: ["piers", "lights", "bait schools"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["schooling", "light-line fish"],
  }),
  species({
    id: "gray_triggerfish",
    slug: "gray-triggerfish",
    commonName: "Gray Triggerfish",
    scientificName: "Balistes capriscus",
    aliases: [],
    family: "triggerfish",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["reef", "wreck", "ocean"],
    regions: ["Gulf Coast", "Southeast Atlantic"],
    summary: "A reef/wreck species that picks at crustaceans and hard structure; useful as a Gulf/Atlantic reef profile.",
    feedingProfile: { primaryPrey: ["crabs", "barnacles", "urchins", "mollusks"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Squid", "Shrimp", "Cut Bait", "Small Jig"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["structure-bound", "crustacean feeder"],
    structureTags: ["reefs", "wrecks", "ledges"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["structure-bound", "crustacean feeder"],
  }),
  species({
    id: "spadefish",
    slug: "spadefish",
    commonName: "Atlantic Spadefish",
    scientificName: "Chaetodipterus faber",
    aliases: [],
    family: "spadefish",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["reef", "wreck", "pier", "inshore"],
    regions: ["Atlantic Coast", "Gulf Coast"],
    summary: "A schooling structure fish often found around pilings, wrecks, buoys, and reefs.",
    feedingProfile: { primaryPrey: ["jellyfish", "small invertebrates", "crustaceans"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Jelly Ball", "Clam", "Small Fly", "Shrimp"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["schooling", "structure-oriented"],
    structureTags: ["pilings", "wrecks", "reefs", "buoys"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["schooling", "structure-oriented"],
  }),
  species({
    id: "tripletail",
    slug: "tripletail",
    commonName: "Tripletail",
    scientificName: "Lobotes surinamensis",
    aliases: [],
    family: "tripletail",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["inshore", "bay", "ocean", "structure"],
    regions: ["Gulf Coast", "Southeast Atlantic"],
    summary: "A floating-structure ambush fish often found near crab pots, buoys, pilings, and surface debris.",
    feedingProfile: { primaryPrey: ["shrimp", "small fish", "crabs"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Live Shrimp", "Soft Plastic", "Small Jig", "Live Bait"],
    bestTechniques: ["Sight fish floating structure", "Approach quietly", "Cast past the fish and bring bait in naturally"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["ambush predator", "structure-oriented"],
    structureTags: ["buoys", "crab pots", "floating debris", "pilings"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["ambush predator", "structure-oriented"],
  }),
  species({
    id: "hogfish",
    slug: "hogfish",
    commonName: "Hogfish",
    scientificName: "Lachnolaimus maximus",
    aliases: [],
    family: "wrasse",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["reef", "wreck", "ocean"],
    regions: ["Florida", "Southeast Atlantic", "Gulf Coast"],
    summary: "A reef wrasse often associated with clear water, sand/reef edges, and crustacean feeding.",
    feedingProfile: { primaryPrey: ["crabs", "shrimp", "mollusks"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Shrimp", "Crab", "Small Jig"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["reef fish", "crustacean feeder"],
    structureTags: ["reefs", "sand edges", "wrecks"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["reef fish", "crustacean feeder"],
  }),
  species({
    id: "sheepshead_california",
    slug: "sheepshead-california",
    commonName: "California Sheephead",
    scientificName: "Semicossyphus pulcher",
    aliases: [],
    family: "wrasse",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["kelp", "reef", "inshore"],
    regions: ["Southern California", "Pacific Coast"],
    summary: "A colorful reef/kelp wrasse associated with rocks, kelp edges, and hard-bottom forage.",
    feedingProfile: { primaryPrey: ["urchins", "crabs", "mollusks"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Shrimp", "Squid", "Crab", "Mussel"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["reef fish", "crustacean feeder"],
    structureTags: ["kelp", "reefs", "rocks"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["reef fish", "crustacean feeder"],
  }),
  species({
    id: "kelp_bass",
    slug: "kelp-bass",
    commonName: "Kelp Bass",
    scientificName: "Paralabrax clathratus",
    aliases: ["Calico Bass"],
    family: "sea bass",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["kelp", "reef", "inshore"],
    regions: ["Southern California", "Baja", "Pacific Coast"],
    summary: "A classic SoCal kelp/reef ambush fish that relates to kelp lanes, current, bait, and hard structure.",
    feedingProfile: { primaryPrey: ["anchovy", "sardine", "squid", "crustaceans"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Swimbait", "Jerkbait", "Live Bait", "Surface Iron"],
    bestTechniques: ["Fish kelp edges", "Work current lanes", "Target bait pushing through structure"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["ambush predator", "kelp-oriented"],
    structureTags: ["kelp edges", "reefs", "boiler rocks", "current seams"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["ambush predator", "kelp-oriented"],
  }),
  species({
    id: "barred_sand_bass",
    slug: "barred-sand-bass",
    commonName: "Barred Sand Bass",
    scientificName: "Paralabrax nebulifer",
    aliases: [],
    family: "sea bass",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["bay", "sand", "reef", "inshore"],
    regions: ["Southern California", "Pacific Coast"],
    summary: "A SoCal bass tied to sandy edges, reefs, harbors, and nearshore structure.",
    feedingProfile: { primaryPrey: ["anchovy", "squid", "small baitfish", "crustaceans"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Swimbait", "Leadhead Squid", "Jig", "Live Bait"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["ambush predator", "bottom-oriented"],
    structureTags: ["sand edges", "reefs", "harbor mouths", "drop-offs"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["ambush predator", "bottom-oriented"],
  }),
  species({
    id: "spotted_bay_bass",
    slug: "spotted-bay-bass",
    commonName: "Spotted Bay Bass",
    scientificName: "Paralabrax maculatofasciatus",
    aliases: [],
    family: "sea bass",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["bay", "harbor", "estuary"],
    regions: ["Southern California", "Baja", "Pacific Coast"],
    summary: "A bay/harbor bass often associated with docks, eelgrass, riprap, moorings, and current seams.",
    feedingProfile: { primaryPrey: ["shrimp", "baitfish", "crabs"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Paddle Tail", "Creature Bait", "Jerkbait", "Blade Bait"],
    bestTechniques: ["Fish docks and eelgrass", "Target current around harbor structure", "Use finesse around pressure"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["ambush predator", "structure-oriented"],
    structureTags: ["docks", "eelgrass", "riprap", "moorings"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["ambush predator", "structure-oriented"],
  }),
  species({
    id: "yellowtail_amberjack_pacific",
    slug: "yellowtail-amberjack-pacific",
    commonName: "California Yellowtail",
    scientificName: "Seriola lalandi",
    aliases: ["Yellowtail"],
    family: "jack",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["kelp", "island", "ocean", "reef"],
    regions: ["Southern California", "Baja", "Pacific Coast"],
    summary: "A hard-fighting pelagic/kelp-edge predator tied to current, bait, islands, and offshore structure.",
    feedingProfile: { primaryPrey: ["sardine", "anchovy", "squid", "mackerel"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Surface Iron", "Live Sardine", "Yo-yo Jig", "Swimbait"],
    bestTechniques: ["Find bait and current", "Fish kelp edges and islands", "Match presentation to fish depth"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["bait-tracker", "roaming predator"],
    structureTags: ["kelp edges", "islands", "current lines", "bait schools"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["bait-tracker", "roaming predator"],
  }),
  species({
    id: "pacific_bonito",
    slug: "pacific-bonito",
    commonName: "Pacific Bonito",
    scientificName: "Sarda chiliensis",
    aliases: [],
    family: "mackerel",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["ocean", "nearshore", "kelp", "inshore"],
    regions: ["Southern California", "Pacific Coast"],
    summary: "A fast schooling predator that tracks bait near beaches, kelp, harbor mouths, and current edges.",
    feedingProfile: { primaryPrey: ["anchovy", "sardine", "small baitfish"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Spoon", "Feather", "Small Jig", "Surface Iron"],
    bestTechniques: ["Retrieve fast", "Cast into foamers or bait", "Use lighter tackle for sport"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["schooling", "bait-tracker"],
    structureTags: ["bait schools", "kelp edges", "harbor mouths"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["schooling", "bait-tracker"],
  }),
  species({
    id: "pacific_barracuda",
    slug: "pacific-barracuda",
    commonName: "Pacific Barracuda",
    scientificName: "Sphyraena argentea",
    aliases: [],
    family: "barracuda",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["nearshore", "kelp", "ocean", "inshore"],
    regions: ["Southern California", "Pacific Coast"],
    summary: "A toothy schooling predator often linked to bait, current, kelp edges, and warm-water windows.",
    feedingProfile: { primaryPrey: ["anchovy", "sardine", "small fish"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Surface Iron", "Spoon", "Jerkbait", "Live Bait"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["schooling", "ambush predator"],
    structureTags: ["kelp edges", "bait schools", "current lines"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["schooling", "ambush predator"],
  }),
  species({
    id: "jacksmelt",
    slug: "jacksmelt",
    commonName: "Jacksmelt",
    scientificName: "Atherinopsis californiensis",
    aliases: [],
    family: "silverside",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["pier", "bay", "surf", "inshore"],
    regions: ["California", "Pacific Coast"],
    summary: "A common pier/bay schooling fish useful for catch logs, light tackle, and forage context.",
    feedingProfile: { primaryPrey: ["plankton", "small crustaceans", "tiny bait"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Sabiki", "Small Shrimp", "Tiny Fly", "Bread"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["schooling", "forage fish"],
    structureTags: ["piers", "harbor edges", "surf zones"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["schooling", "forage fish"],
  }),
  species({
    id: "topsmelt",
    slug: "topsmelt",
    commonName: "Topsmelt",
    scientificName: "Atherinops affinis",
    aliases: [],
    family: "silverside",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["bay", "estuary", "pier", "surf"],
    regions: ["California", "Pacific Coast"],
    summary: "A small schooling silverside common around bays and piers, useful as local ID/forage context.",
    feedingProfile: { primaryPrey: ["plankton", "small invertebrates"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Sabiki", "Tiny Fly", "Small Shrimp"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["schooling", "forage fish"],
    structureTags: ["piers", "harbors", "eelgrass edges"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["schooling", "forage fish"],
  }),
  species({
    id: "sargo",
    slug: "sargo",
    commonName: "Sargo",
    scientificName: "Anisotremus davidsonii",
    aliases: [],
    family: "grunt",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["bay", "reef", "inshore", "pier"],
    regions: ["Southern California", "Baja", "Pacific Coast"],
    summary: "A SoCal grunt often found around rocks, bays, harbors, and mussel/crustacean forage.",
    feedingProfile: { primaryPrey: ["shrimp", "mussels", "small crustaceans"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Shrimp", "Mussel", "Worm", "Small Jig"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["structure-oriented", "crustacean feeder"],
    structureTags: ["rocks", "jetties", "harbors", "reefs"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["structure-oriented", "crustacean feeder"],
  }),
  species({
    id: "zebra_perch",
    slug: "zebra-perch",
    commonName: "Zebra Perch",
    scientificName: "Hermosilla azurea",
    aliases: [],
    family: "sea chub",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["reef", "pier", "inshore"],
    regions: ["Southern California", "Pacific Coast"],
    summary: "A nearshore reef/pier species that adds realistic SoCal light-tackle diversity.",
    feedingProfile: { primaryPrey: ["algae", "small invertebrates"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Peas", "Bread", "Moss", "Small Fly"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["reef fish", "herbivore"],
    structureTags: ["reefs", "jetties", "harbor walls"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["reef fish", "herbivore"],
  }),
  species({
    id: "rubberlip_seaperch",
    slug: "rubberlip-seaperch",
    commonName: "Rubberlip Seaperch",
    scientificName: "Rhacochilus toxotes",
    aliases: [],
    family: "surfperch",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["pier", "reef", "bay", "inshore"],
    regions: ["California", "Pacific Coast"],
    summary: "A perch often found around piers, rocks, and bay structure; a useful local catch-card species.",
    feedingProfile: { primaryPrey: ["shrimp", "worms", "small crustaceans"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Shrimp", "Mussel", "Worm", "Small Jig"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["structure-oriented", "light-tackle fish"],
    structureTags: ["piers", "rocks", "harbor edges"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["structure-oriented", "light-tackle fish"],
  }),
  species({
    id: "shiner_perch",
    slug: "shiner-perch",
    commonName: "Shiner Perch",
    scientificName: "Cymatogaster aggregata",
    aliases: [],
    family: "surfperch",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["bay", "pier", "estuary"],
    regions: ["California", "Pacific Coast"],
    summary: "A small schooling bay/pier perch useful for local ID and beginner catch logs.",
    feedingProfile: { primaryPrey: ["plankton", "small crustaceans", "tiny worms"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Tiny Hook Shrimp", "Sabiki", "Small Worm"],
    bestTechniques: ["Find likely habitat first", "Match bait and water clarity", "Adjust depth before changing spots"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for habitat plus bait before picking a lure.", "Use local rules and access info before targeting."],
    advancedTips: ["Dial in depth, current, and structure before assuming the species is absent.", "Treat one catch or one report as a clue, not a guarantee."],
    behaviorTags: ["schooling", "light-tackle fish"],
    structureTags: ["piers", "eelgrass", "bay edges"],
    depthPreference: "Start where food, cover, comfort, and safe water overlap; adjust depth by light, heat, current, and pressure.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["schooling", "light-tackle fish"],
  }),

  species({
    id: "black_rockfish",
    slug: "black-rockfish",
    commonName: "Black Rockfish",
    scientificName: "Sebastes melanops",
    aliases: ["Black Seaperch", "Black Bass"],
    family: "rockfish",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["reef", "kelp", "inshore", "ocean", "bay"],
    regions: ["Pacific Northwest", "Pacific Coast", "Northern California"],
    summary: "A schooling West Coast rockfish associated with rocky reefs, kelp, jetties, and nearshore structure.",
    feedingProfile: { primaryPrey: ["small fish", "krill", "crustaceans"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Swimbait", "Metal Jig", "Shrimp", "Baitfish"],
    bestTechniques: ["Work rocky structure", "Fish kelp and jetty edges", "Follow bait depth"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for rocky structure and bait before changing presentations.", "Check local rockfish seasons, depth rules, and closures before fishing."],
    advancedTips: ["Use current direction and bait depth to refine the first drift.", "Treat broad regional presence as a clue, not an exact-location guarantee."],
    behaviorTags: ["schooling", "structure-oriented", "bait-tracker"],
    structureTags: ["rocky reefs", "kelp", "jetties", "drop-offs"],
    depthPreference: "Nearshore structure to deeper reef edges depending on season, current, and forage.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["schooling", "structure-oriented", "West Coast rockfish"],
  }),
  species({
    id: "canary_rockfish",
    slug: "canary-rockfish",
    commonName: "Canary Rockfish",
    scientificName: "Sebastes pinniger",
    aliases: ["Orange Rockfish"],
    family: "rockfish",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["reef", "ocean", "inshore", "rock"],
    regions: ["Pacific Northwest", "Pacific Coast", "California Coast"],
    summary: "A bright orange West Coast rockfish associated with reefs, rocky bottom, and deeper nearshore structure.",
    feedingProfile: { primaryPrey: ["small fish", "krill", "crustaceans"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Metal Jig", "Swimbait", "Squid", "Shrimp"],
    bestTechniques: ["Fish rocky bottom", "Vertical jig structure", "Match depth to bait and current"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Use the species profile as a regional guide, not an exact spot claim.", "Check local rockfish regulations and depth restrictions."],
    advancedTips: ["Separate reef depth, current, and bait before assuming a species is absent.", "Use careful ID around similar orange/red rockfish."],
    behaviorTags: ["structure-oriented", "bait-tracker"],
    structureTags: ["reefs", "rocky bottom", "drop-offs"],
    depthPreference: "Often tied to rocky structure and depth bands that shift with season and forage.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["orange rockfish", "structure-oriented", "deep-edge"],
  }),
  species({
    id: "white_seabass",
    slug: "white-seabass",
    commonName: "White Seabass",
    scientificName: "Atractoscion nobilis",
    aliases: ["White Sea Bass"],
    family: "croaker",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["kelp", "inshore", "ocean", "bay"],
    regions: ["Southern California", "California Coast", "Pacific Coast"],
    summary: "A large schooling croaker that follows squid, sardine, anchovy, kelp edges, islands, and current-fed bait lanes.",
    feedingProfile: { primaryPrey: ["squid", "sardine", "anchovy", "mackerel"], notes: "Roddo read: treat this as probabilistic pattern guidance. Start with habitat, bait, structure, season, and local water conditions rather than exact-location claims." },
    bestBaits: ["Live Squid", "Sardine", "Swimbait", "Jig"],
    bestTechniques: ["Follow bait and current", "Fish kelp edges and islands", "Adjust presentation to fish depth"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Bait presence and current matter more than a generic shoreline label.", "Verify current size, season, and area rules before targeting."],
    advancedTips: ["Watch squid and bait concentrations, current seams, and low-light movement.", "Treat one report as a clue rather than a guarantee."],
    behaviorTags: ["schooling", "bait-tracker", "roaming predator"],
    structureTags: ["kelp edges", "islands", "current lines", "bait schools"],
    depthPreference: "Tracks bait from kelp and nearshore lanes into deeper water depending on season and conditions.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["schooling", "bait-tracker", "large croaker"],
  }),

  // Alaska Pack v1: cold-water freshwater and North Pacific species.
  // Broad regional/editorial guidance only; the Alaska regional authority and
  // water-context gates still decide whether each species may enter an Area Read.
  species({
    id: "dolly_varden",
    slug: "dolly-varden",
    commonName: "Dolly Varden",
    scientificName: "Salvelinus malma",
    aliases: ["Dolly"],
    family: "salmonid",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater", "anadromous"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["river", "creek", "lake", "estuary", "bay"],
    regions: ["Alaska"],
    summary: "A cold-water char common in many Alaska river, lake, and coastal systems, with resident and sea-run forms depending on drainage.",
    feedingProfile: { primaryPrey: ["salmon eggs", "insects", "small fish", "crustaceans"], notes: "Roddo read: use cold water, current, spawning-season forage, and connected habitat as the broad pattern rather than an exact-location claim." },
    bestBaits: ["Egg Pattern", "Spoon", "Spinner", "Small Streamer"],
    bestTechniques: ["Work current seams", "Fish behind salmon activity where legal", "Cover lake inlets and outlets"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Start with cold, oxygenated water and current edges.", "Check drainage-specific rules before targeting."],
    advancedTips: ["Separate resident and sea-run habitat before choosing depth and presentation.", "Use forage timing and water temperature to refine the first pass."],
    behaviorTags: ["cold-water", "current-aware feeder", "anadromous"],
    structureTags: ["current seams", "gravel runs", "inlets", "drop-offs"],
    depthPreference: "Cold river lanes, lake edges, and connected coastal water depending on life stage and season.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["cold-water char", "current-aware", "Alaska target"],
  }),
  species({
    id: "lake_trout_alaska",
    slug: "lake-trout-alaska",
    commonName: "Lake Trout",
    scientificName: "Salvelinus namaycush",
    aliases: ["Mackinaw", "Laker"],
    family: "salmonid",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["lake", "reservoir"],
    regions: ["Alaska"],
    summary: "A cold, deep-water char found in northern Alaska lakes where temperature, depth, and forage strongly shape location.",
    feedingProfile: { primaryPrey: ["small fish", "cisco", "whitefish"], notes: "Roddo read: depth, cold water, forage, and seasonal movement matter more than shoreline labels." },
    bestBaits: ["Jig", "Spoon", "Swimbait", "Trolling Plug"],
    bestTechniques: ["Work deep breaks", "Follow forage depth", "Troll or jig cold-water structure"],
    bestSeasons: ["spring", "summer", "fall", "winter"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Use depth and temperature first.", "Look for forage near basin edges and steep breaks."],
    advancedTips: ["Track seasonal vertical movement before changing areas.", "Use bathymetry to separate basin, shelf, and point structure."],
    behaviorTags: ["cold-water", "deep-water", "bait-tracker"],
    structureTags: ["basin edges", "drop-offs", "points", "deep flats"],
    depthPreference: "Often deeper in warm periods and shallower when cold conditions allow; follow forage and thermal comfort.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["cold-water char", "deep-water predator", "Alaska lakes"],
  }),
  species({
    id: "northern_pike_alaska",
    slug: "northern-pike-alaska",
    commonName: "Northern Pike",
    scientificName: "Esox lucius",
    aliases: ["Pike"],
    family: "pike",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["lake", "river", "pond", "marsh", "backwater"],
    regions: ["Alaska"],
    summary: "A large ambush predator found in many northern and interior Alaska waters, especially around vegetation, slack water, and prey-rich edges.",
    feedingProfile: { primaryPrey: ["small fish", "juvenile salmonids", "whitefish"], notes: "Roddo read: use cover, temperature, prey, and connected backwater habitat as broad guidance." },
    bestBaits: ["Spoon", "Spinnerbait", "Swimbait", "Jerkbait"],
    bestTechniques: ["Work weed and backwater edges", "Cover shallow ambush lanes", "Slow down in cold water"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Look for ambush cover and prey before changing lures.", "Use appropriate leader material around toothy fish."],
    advancedTips: ["Separate river backwaters from open-lake structure.", "Use warming shallows and bait concentration to refine the read."],
    behaviorTags: ["ambush predator", "cover-oriented"],
    structureTags: ["weeds", "backwaters", "sloughs", "shallow edges"],
    depthPreference: "Shallow cover and backwaters when conditions allow, with deeper edges becoming more important as water warms or clears.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["ambush predator", "toothy fish", "Alaska freshwater"],
  }),
  species({
    id: "sheefish",
    slug: "sheefish",
    commonName: "Sheefish",
    scientificName: "Stenodus leucichthys",
    aliases: ["Inconnu"],
    family: "salmonid",
    category: "freshwater",
    aquaticKind: 'finfish',
    waterRealms: ["freshwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["river", "lake"],
    regions: ["Alaska"],
    summary: "A large northern whitefish relative associated with major Alaska river systems, current corridors, and seasonal movement.",
    feedingProfile: { primaryPrey: ["small fish", "whitefish", "smelt"], notes: "Roddo read: treat major-river connectivity, current, forage, and seasonal movement as the broad pattern." },
    bestBaits: ["Spoon", "Spinner", "Swimbait", "Streamer"],
    bestTechniques: ["Work current seams", "Cover deep river bends", "Follow bait movement"],
    bestSeasons: ["summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Use this as a major-river regional read rather than a generic Alaska lake fish.", "Check local access and regulations."],
    advancedTips: ["Focus on current corridors and migration staging water.", "Use depth changes and bait concentration before expanding the search."],
    behaviorTags: ["river migrant", "bait-tracker", "current-aware feeder"],
    structureTags: ["river bends", "current seams", "deep holes", "channel edges"],
    depthPreference: "Large river channels, deep bends, and connected lake-river corridors depending on season.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["large river fish", "current-aware", "Alaska native"],
  }),
  species({
    id: "yelloweye_rockfish_alaska",
    slug: "yelloweye-rockfish-alaska",
    commonName: "Yelloweye Rockfish",
    scientificName: "Sebastes ruberrimus",
    aliases: ["Yelloweye"],
    family: "rockfish",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "education_sensitive",
    habitats: ["reef", "rock", "inshore", "ocean"],
    regions: ["Alaska", "North Pacific"],
    summary: "A long-lived North Pacific rockfish associated with rocky reefs, steep relief, and deeper structure; careful identification and current rules matter.",
    feedingProfile: { primaryPrey: ["small fish", "crabs", "shrimp"], notes: "Roddo read: use rocky relief, depth, current, and forage as broad habitat guidance and verify current regulations before fishing." },
    bestBaits: ["Jig", "Swimbait", "Squid", "Herring"],
    bestTechniques: ["Fish rocky relief", "Keep presentations near structure", "Use careful species ID"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Treat rockfish ID and regulations as part of the fishing plan.", "Use depth and hard-bottom relief before assuming presence."],
    advancedTips: ["Refine by slope, relief, and current rather than generic offshore depth.", "Avoid presenting this as an exact-spot claim."],
    behaviorTags: ["structure-oriented", "bottom-oriented"],
    structureTags: ["rocky reefs", "steep relief", "drop-offs", "ledges"],
    depthPreference: "Rocky reefs and deeper hard-bottom structure, with depth varying by area and season.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["long-lived rockfish", "structure-oriented", "North Pacific"],
  }),
  species({
    id: "dusky_rockfish_alaska",
    slug: "dusky-rockfish-alaska",
    commonName: "Dusky Rockfish",
    scientificName: "Sebastes ciliatus",
    aliases: ["Dusky Rockfish"],
    family: "rockfish",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["reef", "rock", "inshore", "ocean"],
    regions: ["Alaska", "Gulf of Alaska", "North Pacific"],
    summary: "A Gulf of Alaska rockfish associated with rocky bottom, reefs, and current-fed structure.",
    feedingProfile: { primaryPrey: ["small fish", "krill", "crustaceans"], notes: "Roddo read: use reef relief, current, depth, and bait as broad habitat evidence." },
    bestBaits: ["Metal Jig", "Swimbait", "Herring", "Squid"],
    bestTechniques: ["Vertical jig rocky structure", "Follow bait depth", "Work current-facing reef edges"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Start with rocky structure and bait.", "Verify local rockfish regulations and identification."],
    advancedTips: ["Use relief and current direction to choose the first drift.", "Adjust depth before abandoning productive structure."],
    behaviorTags: ["structure-oriented", "schooling", "bait-tracker"],
    structureTags: ["rocky reefs", "ledges", "drop-offs", "current edges"],
    depthPreference: "Rocky reef and hard-bottom depth bands that shift with forage and current.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["Alaska rockfish", "structure-oriented", "bait-tracker"],
  }),
  species({
    id: "northern_rockfish_alaska",
    slug: "northern-rockfish-alaska",
    commonName: "Northern Rockfish",
    scientificName: "Sebastes polyspinis",
    aliases: ["Northern Rockfish"],
    family: "rockfish",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["reef", "rock", "ocean"],
    regions: ["Alaska", "Gulf of Alaska", "North Pacific"],
    summary: "A North Pacific rockfish associated with offshore rocky habitat and deeper reef structure.",
    feedingProfile: { primaryPrey: ["krill", "small fish", "crustaceans"], notes: "Roddo read: use depth, hard-bottom relief, current, and forage as broad evidence." },
    bestBaits: ["Metal Jig", "Swimbait", "Squid"],
    bestTechniques: ["Fish deep rocky relief", "Vertical jig", "Track bait and current"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Use bathymetry and structure first.", "Check local rockfish regulations and identification."],
    advancedTips: ["Separate broad basin depth from actual hard-bottom relief.", "Use current-facing edges to prioritize drifts."],
    behaviorTags: ["structure-oriented", "schooling"],
    structureTags: ["rocky reefs", "deep ledges", "slope", "drop-offs"],
    depthPreference: "Deeper rocky structure and relief zones, refined by current and forage.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["North Pacific rockfish", "deep structure", "Alaska"],
  }),
  species({
    id: "pacific_ocean_perch_alaska",
    slug: "pacific-ocean-perch-alaska",
    commonName: "Pacific Ocean Perch",
    scientificName: "Sebastes alutus",
    aliases: ["POP"],
    family: "rockfish",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["ocean", "reef", "rock"],
    regions: ["Alaska", "Gulf of Alaska", "Bering Sea", "North Pacific"],
    summary: "A deeper-water North Pacific rockfish associated with shelf and slope structure, schools, and offshore relief.",
    feedingProfile: { primaryPrey: ["krill", "small fish", "copepods"], notes: "Roddo read: use shelf/slope depth, relief, and forage as broad context; this is not an exact-location claim." },
    bestBaits: ["Metal Jig", "Small Swimbait", "Squid"],
    bestTechniques: ["Fish deeper relief", "Vertical jig", "Follow schooling bait"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["This is primarily a deeper-water structure read.", "Verify local rules and species ID."],
    advancedTips: ["Use shelf breaks and relief instead of treating all offshore depth equally.", "Current and bait depth should refine presentation."],
    behaviorTags: ["schooling", "deep-water", "structure-oriented"],
    structureTags: ["shelf edge", "slope", "rocky relief", "drop-offs"],
    depthPreference: "Deeper shelf and slope structure with schools often tied to relief and forage.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["deep-water rockfish", "schooling", "North Pacific"],
  }),
  species({
    id: "pacific_cod_alaska",
    slug: "pacific-cod-alaska",
    commonName: "Pacific Cod",
    scientificName: "Gadus macrocephalus",
    aliases: ["Gray Cod"],
    family: "cod",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "common_target",
    habitats: ["ocean", "inshore", "bay"],
    regions: ["Alaska", "Gulf of Alaska", "Bering Sea", "North Pacific"],
    summary: "A cold-water bottom-oriented cod found across Alaska marine waters, often associated with shelf habitat, soft bottom, edges, and forage.",
    feedingProfile: { primaryPrey: ["crabs", "shrimp", "small fish", "worms"], notes: "Roddo read: use bottom depth, substrate transition, current, and forage as broad habitat guidance." },
    bestBaits: ["Jig", "Herring", "Squid", "Cut Bait"],
    bestTechniques: ["Fish near bottom", "Work shelf and channel edges", "Adjust weight to current"],
    bestSeasons: ["spring", "summer", "fall", "winter"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Keep the presentation near bottom and use depth/structure to guide the first pass.", "Check current Alaska sport-fishing regulations."],
    advancedTips: ["Use current and bottom transition to refine drift position.", "Separate soft-bottom feeding lanes from hard reef species."],
    behaviorTags: ["bottom-oriented", "cold-water", "forage-tracker"],
    structureTags: ["shelf", "channel edges", "soft-bottom transitions", "drop-offs"],
    depthPreference: "Bottom-oriented across shelf and coastal depth bands, refined by forage, substrate, and current.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["bottom fish", "cold-water cod", "Alaska marine"],
  }),
  species({
    id: "sablefish_alaska",
    slug: "sablefish-alaska",
    commonName: "Sablefish",
    scientificName: "Anoplopoma fimbria",
    aliases: ["Black Cod"],
    family: "sablefish",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "trophy",
    habitats: ["ocean", "deep", "slope"],
    regions: ["Alaska", "North Pacific"],
    summary: "A deep-water North Pacific predator associated with continental slope habitat, deep relief, and cold offshore water.",
    feedingProfile: { primaryPrey: ["small fish", "squid", "crustaceans"], notes: "Roddo read: deep-water depth, slope, relief, and forage should drive this profile rather than generic coastal proximity." },
    bestBaits: ["Herring", "Squid", "Jig", "Cut Bait"],
    bestTechniques: ["Fish deep structure", "Hold near bottom", "Use current-aware vertical presentations"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["day", "dusk"],
    beginnerTips: ["Treat this as a deep-water specialist, not a normal inshore fish.", "Verify current regulations and safe offshore conditions."],
    advancedTips: ["Use slope and relief rather than raw depth alone.", "Match tackle and presentation to current at depth."],
    behaviorTags: ["deep-water", "bottom-oriented", "cold-water"],
    structureTags: ["continental slope", "deep relief", "drop-offs", "basin edge"],
    depthPreference: "Deep offshore slope and relief habitat; FishIQ should only strengthen this read when bathymetry supports it.",
    activeTimes: ["day", "dusk"],
    keyTraits: ["deep-water predator", "North Pacific", "black cod"],
  }),
  species({
    id: "walleye_pollock_alaska",
    slug: "walleye-pollock-alaska",
    commonName: "Walleye Pollock",
    scientificName: "Gadus chalcogrammus",
    aliases: ["Pollock", "Alaska Pollock"],
    family: "cod",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["ocean", "inshore"],
    regions: ["Alaska", "Bering Sea", "Gulf of Alaska", "North Pacific"],
    summary: "A schooling North Pacific cod relative common in Alaska marine ecosystems and often tied to bait-rich midwater and shelf zones.",
    feedingProfile: { primaryPrey: ["krill", "small fish", "copepods"], notes: "Roddo read: use open-water depth, shelf context, current, and bait as broad evidence." },
    bestBaits: ["Small Jig", "Herring Strip", "Sabiki"],
    bestTechniques: ["Follow bait depth", "Jig through schools", "Use current and sonar-like depth context where available"],
    bestSeasons: ["spring", "summer", "fall", "winter"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["Treat this as a schooling open-water/shelf read.", "Check local regulations and target context."],
    advancedTips: ["Depth and bait concentration matter more than shoreline structure.", "Use current layers to refine vertical presentation."],
    behaviorTags: ["schooling", "bait-tracker", "open-water"],
    structureTags: ["shelf", "bait schools", "basin edges"],
    depthPreference: "Midwater to near-bottom shelf zones depending on season, forage, and current.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["schooling", "North Pacific", "cod relative"],
  }),
  species({
    id: "atka_mackerel_alaska",
    slug: "atka-mackerel-alaska",
    commonName: "Atka Mackerel",
    scientificName: "Pleurogrammus monopterygius",
    aliases: ["Atka"],
    family: "greenling",
    category: "saltwater",
    aquaticKind: 'finfish',
    waterRealms: ["saltwater"],
    harvestModes: ['rod_and_reel'],
    fishiqEligible: true,
    fishIQTier: "local_common",
    habitats: ["reef", "ocean", "rock"],
    regions: ["Alaska", "Aleutian Islands", "Bering Sea", "North Pacific"],
    summary: "A North Pacific schooling fish strongly associated with rocky Aleutian and offshore structure.",
    feedingProfile: { primaryPrey: ["krill", "copepods", "small fish"], notes: "Roddo read: use rocky offshore structure, current, and schooling forage as broad regional context." },
    bestBaits: ["Small Jig", "Metal Jig", "Baitfish"],
    bestTechniques: ["Jig rocky structure", "Follow schools", "Work current-facing reef edges"],
    bestSeasons: ["spring", "summer", "fall"],
    bestTimesOfDay: ["dawn", "day", "dusk"],
    beginnerTips: ["This should read as an Aleutian/offshore structure fish, not a generic Alaska bay species.", "Check local rules and target context."],
    advancedTips: ["Use current-facing rock and reef relief to refine the read.", "Schooling depth and bait should drive presentation."],
    behaviorTags: ["schooling", "structure-oriented", "current-aware"],
    structureTags: ["rocky reefs", "island structure", "current edges"],
    depthPreference: "Rocky offshore and island structure, with depth refined by current and schooling forage.",
    activeTimes: ["dawn", "day", "dusk"],
    keyTraits: ["schooling", "Aleutian", "North Pacific"],
  }),

];
